import React from 'react';
import './FoodConfig.css';

function FoodConfig({ pendingFoodConfig, setPendingFoodConfig, setFoodSearchComment, isMobile }) {
  const cuisineOptions = [
    'Authentic', 'Fusion', 'Italian', 'Chinese', 'Japanese', 'Mexican', 'Indian', 'French', 'Thai', 'Greek', 
    'American', 'Mediterranean', 'Korean', 'Vietnamese', 'Spanish', 'Lebanese', 'Turkish'
  ];

  const priceOptions = [
    { value: 'low-range', label: 'Low-range' },
    { value: 'mid-range', label: 'Mid-range' },
    { value: 'upscale', label: 'Upscale' }
  ];

  const experienceOptions = [
    'Casual', 'Romantic', 'Family-friendly', 'Trendy', 'Quiet', 'Scenic'
  ];

  // Check if "Everything" is selected
  const isEverythingSelected = pendingFoodConfig.cuisineTypes.includes('Everything');

  const handleCuisineChange = (cuisine) => {
    if (cuisine === 'Everything') {
      if (isEverythingSelected) {
        // If "Everything" is currently selected, deselect it and enable all other options
        setPendingFoodConfig(cfg => ({ 
          ...cfg, 
          cuisineTypes: cfg.cuisineTypes.filter(c => c !== 'Everything')
        }));
      } else {
        // If "Everything" is not selected, select it and clear all other selections
        setPendingFoodConfig(cfg => ({ ...cfg, cuisineTypes: ['Everything'] }));
      }
    } else {
      // Regular cuisine selection - only allow if "Everything" is not selected
      if (!isEverythingSelected) {
        const newCuisines = pendingFoodConfig.cuisineTypes.includes(cuisine)
          ? pendingFoodConfig.cuisineTypes.filter(c => c !== cuisine)
          : [...pendingFoodConfig.cuisineTypes, cuisine];
        setPendingFoodConfig(cfg => ({ ...cfg, cuisineTypes: newCuisines }));
      }
    }
  };

  if (isMobile) {
    return (
      <>
        <div className="food-config-sliders-row-mobile">
          {/* Price Range */}
          <div className="food-config-slider-mobile">
            <label>Price</label>
            <select
              value={pendingFoodConfig.priceRange}
              onChange={e => setPendingFoodConfig(cfg => ({ ...cfg, priceRange: e.target.value }))}
            >
              {priceOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Experience */}
          <div className="food-config-slider-mobile">
            <label>Experience</label>
            <select
              value={pendingFoodConfig.experience}
              onChange={e => setPendingFoodConfig(cfg => ({ ...cfg, experience: e.target.value }))}
            >
              {experienceOptions.map(exp => (
                <option key={exp} value={exp}>{exp}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Cuisine Types - Mobile-friendly multi-select */}
        <div className="food-config-cuisine-section-mobile">
          <label className="food-config-cuisine-label-mobile">Cuisine Types:</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            {/* Dropdown options - Left side */}
            <div style={{
              width: '140px',
              flexShrink: 0,
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: 'white',
              maxHeight: '90px',
              overflowY: 'auto'
            }}>
              {/* Everything option */}
              <div
                onClick={() => {
                  if (pendingFoodConfig.cuisineTypes.includes('Everything')) {
                    // Deselect "Everything"
                    setPendingFoodConfig(cfg => ({ ...cfg, cuisineTypes: [] }));
                  } else {
                    // Select "Everything" and clear all others
                    setPendingFoodConfig(cfg => ({ ...cfg, cuisineTypes: ['Everything'] }));
                  }
                }}
                style={{
                  padding: '8px',
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  backgroundColor: pendingFoodConfig.cuisineTypes.includes('Everything') ? '#e3f2fd' : 'white',
                  fontWeight: 'bold',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  userSelect: 'none'
                }}
              >
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid #2196F3',
                  borderRadius: '2px',
                  backgroundColor: pendingFoodConfig.cuisineTypes.includes('Everything') ? '#2196F3' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '8px',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {pendingFoodConfig.cuisineTypes.includes('Everything') ? '✓' : ''}
                </div>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Everything
                </span>
              </div>

              {/* Individual cuisine options */}
              {cuisineOptions.map(cuisine => (
                <div
                  key={cuisine}
                  onClick={() => {
                    if (isEverythingSelected) {
                      // If "Everything" is selected, don't allow individual selections
                      return;
                    }
                    
                    if (pendingFoodConfig.cuisineTypes.includes(cuisine)) {
                      // Remove cuisine
                      const newCuisines = pendingFoodConfig.cuisineTypes.filter(c => c !== cuisine);
                      setPendingFoodConfig(cfg => ({ ...cfg, cuisineTypes: newCuisines }));
                    } else {
                      // Add cuisine
                      const newCuisines = [...pendingFoodConfig.cuisineTypes, cuisine];
                      setPendingFoodConfig(cfg => ({ ...cfg, cuisineTypes: newCuisines }));
                    }
                  }}
                  style={{
                    padding: '8px',
                    borderBottom: cuisineOptions.indexOf(cuisine) === cuisineOptions.length - 1 ? 'none' : '1px solid #eee',
                    cursor: isEverythingSelected ? 'not-allowed' : 'pointer',
                    backgroundColor: pendingFoodConfig.cuisineTypes.includes(cuisine) ? '#e8f5e8' : 'white',
                    opacity: isEverythingSelected ? 0.5 : 1,
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    userSelect: 'none'
                  }}
                >
                  <div style={{
                    width: '14px',
                    height: '14px',
                    border: `2px solid ${isEverythingSelected ? '#ccc' : '#4CAF50'}`,
                    borderRadius: '2px',
                    backgroundColor: pendingFoodConfig.cuisineTypes.includes(cuisine) ? '#4CAF50' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '8px',
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}>
                    {pendingFoodConfig.cuisineTypes.includes(cuisine) ? '✓' : ''}
                  </div>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cuisine}
                  </span>
                </div>
              ))}
            </div>

            {/* Selected items display - Right side */}
            <div style={{
              flex: 1,
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '8px',
              backgroundColor: 'white',
              minHeight: '40px'
            }}>
              {pendingFoodConfig.cuisineTypes.length === 0 ? (
                <span style={{ color: '#999', fontSize: '14px' }}>Select cuisine types...</span>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {pendingFoodConfig.cuisineTypes.map(cuisine => (
                    <span
                      key={cuisine}
                      style={{
                        background: cuisine === 'Everything' ? '#2196F3' : '#4CAF50',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {cuisine}
                      <button
                        onClick={() => {
                          if (cuisine === 'Everything') {
                            // If removing "Everything", clear selection and enable others
                            setPendingFoodConfig(cfg => ({ ...cfg, cuisineTypes: [] }));
                          } else {
                            // Remove individual cuisine
                            const newCuisines = pendingFoodConfig.cuisineTypes.filter(c => c !== cuisine);
                            setPendingFoodConfig(cfg => ({ ...cfg, cuisineTypes: newCuisines }));
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          padding: '0',
                          fontSize: '14px',
                          lineHeight: '1',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  } else {
    return (
      <>
        {/* Desktop Food Configuration */}
        <div className="food-config-desktop-container">
          {/* Price Range */}
          <div className="food-config-attribute-container">
            <label className="food-config-attribute-label">
              Price
            </label>
            <select
              value={pendingFoodConfig.priceRange}
              onChange={e => setPendingFoodConfig(cfg => ({ ...cfg, priceRange: e.target.value }))}
              className="food-config-select"
            >
              {priceOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Experience */}
          <div className="food-config-attribute-container">
            <label className="food-config-experience-label">
              Experience
            </label>
            <select
              value={pendingFoodConfig.experience}
              onChange={e => setPendingFoodConfig(cfg => ({ ...cfg, experience: e.target.value }))}
              className="food-config-select"
            >
              {experienceOptions.map(exp => (
                <option key={exp} value={exp}>{exp}</option>
              ))}
            </select>
          </div>

          {/* Tuning Comments - Inline with Price and Experience */}
          <div className="food-config-comments-container">
            <label className="food-config-comments-label">
              Comments
            </label>
            <input
              type="text"
              placeholder="Tuning comments"
              value={pendingFoodConfig.searchComment}
              onChange={e => {
                setFoodSearchComment(e.target.value);
                setPendingFoodConfig(cfg => ({ ...cfg, searchComment: e.target.value }));
              }}
              className="food-config-comments-input"
            />
          </div>
        </div>

        {/* Cuisine Types - Multi-select for Desktop */}
        <div className="food-config-cuisine-section-desktop">
          <label className="food-config-cuisine-label-desktop">Cuisine Types:</label>
          <div className="food-config-cuisine-grid-desktop">
            {/* Everything option */}
            <label 
              className={`food-config-cuisine-item-desktop ${
                pendingFoodConfig.cuisineTypes.includes('Everything') ? 'selected' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={pendingFoodConfig.cuisineTypes.includes('Everything')}
                onChange={() => handleCuisineChange('Everything')}
              />
              Everything
            </label>
            {cuisineOptions.map(cuisine => (
              <label 
                key={cuisine} 
                className={`food-config-cuisine-item-desktop ${
                  pendingFoodConfig.cuisineTypes.includes(cuisine) ? 'selected' : ''
                } ${isEverythingSelected ? 'disabled' : ''}`}
                style={{ opacity: isEverythingSelected ? 0.5 : 1 }}
              >
                <input
                  type="checkbox"
                  checked={pendingFoodConfig.cuisineTypes.includes(cuisine)}
                  onChange={() => handleCuisineChange(cuisine)}
                  disabled={isEverythingSelected}
                />
                {cuisine}
              </label>
            ))}
          </div>
        </div>
      </>
    );
  }
}

export default FoodConfig;
